import { actions as DocumentActions } from '~app/features/document';
import canvasSlice from './slice';
import { CANVAS_SIZE, DEFAULT_ZOOM_LEVEL, ZOOM_LEVELS } from './consts';

const PADDING = 20;

// zoom canvas to fit all objects in the document (or as much as possible)
export const zoomToFit = () => (dispatch, getState) => {
  const doc = getState().document.present?.workingModel;
  if (!doc) return;
  const { viewportSize, offset } = getState().canvas;

  // default: canvas centered at 100%
  let zoomLevel = DEFAULT_ZOOM_LEVEL;
  let offset_x = Math.max(0, Math.floor((CANVAS_SIZE - viewportSize.x) / 2));
  let offset_y = Math.max(0, Math.floor((CANVAS_SIZE - viewportSize.y) / 2));

  const bb = { left: CANVAS_SIZE, top: CANVAS_SIZE, right: -1, bottom: -1 };

  const update_bb = (position, padLeft, padRight, padTop, padBottom) => {
    if (position.x - padLeft < bb.left) bb.left = position.x - padLeft;
    if (position.x + padRight > bb.right) bb.right = position.x + padRight;
    if (position.y - padTop < bb.top) bb.top = position.y - padTop;
    if (position.y + padBottom > bb.bottom) bb.bottom = position.y + padBottom;
  };

  Object.keys(doc.nodes).forEach(label => {
    const node = doc.nodes[label];
    update_bb(node.position, 75, 75, 40, 75);
    if (node.arrowHeadPosition)
      update_bb(node.arrowHeadPosition, 15, 15, 15, 15);
    if (node.arrowTailPosition)
      update_bb(node.arrowTailPosition, 15, 15, 15, 15);
  });

  doc.signals.forEach(signal => update_bb(signal.position, 12, 12, 12, 12));

  if (bb.right !== -1) {
    const width = bb.right - bb.left;
    const height = bb.bottom - bb.top;

    // find the highest zoom level (not larger than 100%) that
    // fits everything
    for (; zoomLevel >= 0; zoomLevel--) {
      if (
        width <= (viewportSize.x - PADDING) / ZOOM_LEVELS[zoomLevel] &&
        height <= (viewportSize.y - PADDING) / ZOOM_LEVELS[zoomLevel]
      ) {
        break;
      }
    }

    const center_x =
      (bb.left + (bb.right - bb.left) / 2) * ZOOM_LEVELS[zoomLevel];
    const center_y =
      (bb.top + (bb.bottom - bb.top) / 2) * ZOOM_LEVELS[zoomLevel];
    offset_x = center_x - viewportSize.x / 2;
    offset_y = center_y - viewportSize.y / 2;
    offset_x = Math.max(0, Math.min(CANVAS_SIZE - viewportSize.x, offset_x));
    offset_y = Math.max(0, Math.min(CANVAS_SIZE - viewportSize.y, offset_y));
  }

  const args = { zoomLevel };
  if (offset_x !== offset.x) args.offset_x = offset_x;
  if (offset_y !== offset.y) args.offset_y = offset_y;
  dispatch(canvasSlice.actions.setZoom(args));
};

export const selectItem = ({ type, label, from, to }) => (
  dispatch,
  getState,
) => {
  const { selection } = getState().canvas;
  const { nodes } = getState().document.present.workingModel;
  if (!type || !selection.latched) {
    dispatch(
      canvasSlice.actions.setSelection({
        type,
        label,
        from,
        to,
        latched: false,
      }),
    );
    return;
  }
  if (selection.latched) {
    const { action, ...args } = selection.latchData;
    if (action === 'addSignal') {
      if (type !== 'node') return;
      if (
        nodes[label].type !== 'flow' &&
        nodes[label].type !== 'variable' &&
        nodes[label].type !== 'stock' &&
        nodes[label].type !== 'generic'
      )
        return;
      if (selection.label !== label) {
        dispatch(
          DocumentActions.addSignal({
            polarity: args.polarity,
            fromLabel: selection.label,
            toLabel: label,
          }),
        );
      }

      dispatch(canvasSlice.actions.setSelection({ latched: false }));
      return;
    } else if (action === 'connectFlow') {
      if (type !== 'node') return;
      if (nodes[label].type !== 'stock') return;
      const flow = selection.label;
      const other =
        nodes[flow][args.direction === 'to' ? 'connectFrom' : 'connectTo'];
      if (other === label) return; // cannot have same node as both from and to
      dispatch(
        DocumentActions.connectFlow({
          flowLabel: flow,
          [args.direction + 'Label']: label,
        }),
      );
      // select the flow again
      dispatch(
        canvasSlice.actions.setSelection({
          type: 'node',
          label: flow,
          latched: false,
        }),
      );
      return;
    }
    dispatch(canvasSlice.actions.setSelection({ latched: false }));
  }
  dispatch(canvasSlice.actions.setSelection({ type, label, from, to }));
};

// Add the given label to the canvas. The given x,y should be the location relative
// to the viewport. If no x,y is given label is placed at center.
export const dropLabel = ({ label, x, y }) => (dispatch, getState) => {
  const { viewportSize, offset, scale } = getState().canvas;
  if (!x || !y) {
    x = (viewportSize.x / 2 + offset.x) / scale;
    y = (viewportSize.y / 2 + offset.y) / scale;
    //offset the label if it is too close to another
    ({ x, y } = DocumentActions.getNewLabelCoords(x, y, getState));
  } else {
    x = (x + offset.x) / scale;
    y = (y + offset.y) / scale;
  }

  dispatch(DocumentActions.placeLabel({ label, x, y }));
  // select the new node
  dispatch(
    canvasSlice.actions.setSelection({ type: 'node', label, latched: false }),
  );
};
